import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { CircleAlert, Plus, Store } from 'lucide-react';
import './DashboardPage.css';
import TicketTableDashboard from './TicketTableDashboard';
import DashboardInsights from './DashboardInsights';
import { Banner, Modal, PageHeader, Toast } from '../design/ui';
import { fetchPreferences, orderFor, ORDER_ALL } from '../utils/preferences';

const NS = 'ticketListsUi';

const EDN_TICKET_TOPICS = [
    { id: 22, topic_full: "Complément d'adresse" },
    { id: 23, topic_full: "Retard de livraison / demande de localisation" },
    { id: 24, topic_full: "Demande de retour colis" },
    { id: 25, topic_full: "Colis bloqué en douane" },
    { id: 30, topic_full: "Demande de preuve de livraison / Confirmation de livraison" },
    { id: 31, topic_full: "Demande de preuve de livraison / Obtenir une preuve de livraison" },
    { id: 32, topic_full: "Marchandise manquante dans le colis" },
    { id: 33, topic_full: "Marchandise endommagée" },
    { id: 34, topic_full: "Contestation de livraison" },
    { id: 35, topic_full: "Retour non reçu" },
    { id: 36, topic_full: "Facturation et paiement" },
    { id: 37, topic_full: "Autre" },
    { id: 38, topic_full: "Colis en retard donnant lieu à une indemnisation" },
    { id: 39, topic_full: "Colis déposé au mauvais point relais" },
    { id: 40, topic_full: "Multi colis non respecté" },
    { id: 41, topic_full: "Problème d'enlèvement de votre marchandise" },
    { id: 42, topic_full: "Colis en souffrance" },
];

// GET /api/tickets/stats feeds the status tiles; it is cheap (index-covered)
// but every ticket event schedules a list refresh: re-read it at most once a
// minute on those, at once after a bulk action of this page.
const STATS_REFRESH_MIN_INTERVAL_MS = 60000;

const isCount = (value) => typeof value === 'number' && Number.isFinite(value);

// Team figures (À traiter, Activité): account owners, every employee (Kevin
// 29/10: plain employees see « À traiter » too — GET
// /api/tickets/dashboard-insights already answers them, scoped to their
// account) and EDN accounts.
export const canSeeTeamInsights = (user) => Boolean(
    user?.edn_api_account || user?.role === 'admin' || user?.role === 'employee'
);

const fmtFr = (value) => Number(value).toLocaleString('fr-FR').replace(/\u202f/g, '\u00a0');

const Dashboard = () => {
    const [user, setUser] = useState(undefined);
    const [tickets, setTickets] = useState([]);
    const [assignedTickets, setAssignedTickets] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [assignedCurrentPage, setAssignedCurrentPage] = useState(1);
    const [paginationInfo, setPaginationInfo] = useState(null);
    const [assignedPaginationInfo, setAssignedPaginationInfo] = useState(null);
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [notificationState, setNotificationState] = useState({ message: '', success: true, visible: false });
    // « Activité » period selector: sizes the KPI windows of DashboardInsights.
    const [windowDays, setWindowDays] = useState(7);
    // Active « À traiter » reason (backend attention_reason filter on the lists).
    const [attentionReason, setAttentionReason] = useState(null);
    // What the header line reads from the insights answer (null = unavailable).
    const [insightsSummary, setInsightsSummary] = useState(undefined);
    // Status counters of GET /api/tickets/stats (null = unavailable).
    const [ticketStats, setTicketStats] = useState(undefined);

    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const tl = (key, fallback, options) => t(`${NS}.${key}`, fallback, options);
    const [searchParams, setSearchParams] = useSearchParams();

    // Server-side sort of the ticket tables (KI-111): the list endpoints
    // whitelist these values, so an unknown URL value falls back to the default.
    const TICKET_SORT_FIELDS = ['updated_at', 'created_at', 'sentiment_score'];
    const readSortFromUrl = () => {
        const sortField = searchParams.get('sortField');
        const sortOrder = searchParams.get('sortOrder');
        return {
            sort_by: TICKET_SORT_FIELDS.includes(sortField) ? sortField : 'updated_at',
            order: sortOrder === 'asc' ? 'asc' : 'desc'
        };
    };
    const appendSortParams = (queryParams, filters) => {
        const sortBy = TICKET_SORT_FIELDS.includes(filters?.sort_by) ? filters.sort_by : 'updated_at';
        const order = filters?.order === 'asc' ? 'asc' : 'desc';
        queryParams.append('sort_by', sortBy);
        queryParams.append('order', order);
    };
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    // KI-242: the page renders as soon as the user is known; the ticket list
    // shows its own placeholder until its first answer.
    const [ticketsLoading, setTicketsLoading] = useState(true);

    // Brand-related state
    const [brands, setBrands] = useState([]);
    // Brand ids are Mongo ObjectId STRINGS (BrandResponse.id) — never parseInt.
    const [selectedBrand, setSelectedBrand] = useState(searchParams.get('selectedBrand') || null);

    const [currentFilters, setCurrentFilters] = useState({
        topics: [],
        separate_assigned: true
    });

    // 'unassigned' | 'assigned' | 'my_assigned' | 'view'
    const [currentViewMode, setCurrentViewMode] = useState('unassigned');

    // The EDN ticket creation dialog
    const [isCreateTicketOverlayOpen, setIsCreateTicketOverlayOpen] = useState(false);
    const [newTicketData, setNewTicketData] = useState({ tracking: '', message: '', ticket_topic_id: '' });
    const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
    const [submitTicketError, setSubmitTicketError] = useState(null);
    const [submitTicketSuccess, setSubmitTicketSuccess] = useState(null);

    const isSearchActiveRef = useRef(false);
    const currentFiltersRef = useRef(currentFilters);
    const currentPageRef = useRef(currentPage);
    const lastTicketsRefreshRef = useRef(0);
    const pendingRefreshTimeoutRef = useRef(null);
    const lastStatsRefreshRef = useRef(0);
    // 10 s: every tenant event (incl. widget messages) schedules a list refresh;
    // it is the heaviest endpoint (p95 ~0.5 s), so coalesce a little more.
    const TICKETS_REFRESH_MIN_INTERVAL_MS = 10000;

    const fetchStats = async (token, { force = false } = {}) => {
        const now = Date.now();
        if (!force && now - lastStatsRefreshRef.current < STATS_REFRESH_MIN_INTERVAL_MS) return;
        lastStatsRefreshRef.current = now;
        try {
            const response = await axios.get('/api/tickets/stats', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTicketStats(response.data || null);
        } catch (error) {
            console.error('Failed to fetch ticket stats:', error);
            setTicketStats((previous) => (previous === undefined ? null : previous));
        }
    };

    // Websocket refresh messages arrive for every ticket event; without a
    // debounce each one re-ran the full tickets fetch against the backend.
    const scheduleTicketsRefresh = () => {
        const runRefresh = () => {
            const token = localStorage.getItem('access_token');
            if (!token) return;
            lastTicketsRefreshRef.current = Date.now();
            fetchTickets(token, currentPageRef.current, currentFiltersRef.current);
            fetchStats(token);
        };

        const elapsed = Date.now() - lastTicketsRefreshRef.current;
        if (elapsed >= TICKETS_REFRESH_MIN_INTERVAL_MS) {
            runRefresh();
            return;
        }
        if (pendingRefreshTimeoutRef.current) return; // trailing refresh already scheduled
        pendingRefreshTimeoutRef.current = setTimeout(() => {
            pendingRefreshTimeoutRef.current = null;
            runRefresh();
        }, TICKETS_REFRESH_MIN_INTERVAL_MS - elapsed);
    };

    useEffect(() => {
        isSearchActiveRef.current = isSearchActive;
    }, [isSearchActive]);

    useEffect(() => {
        currentFiltersRef.current = currentFilters;
    }, [currentFilters]);

    useEffect(() => {
        currentPageRef.current = currentPage;
    }, [currentPage]);

    const connectWebSocket = () => {
        const token = localStorage.getItem('access_token');
        if (!token) return;

        // Check token expiration before connecting
        try {
            const tokenPayload = JSON.parse(atob(token.split('.')[1]));
            if (tokenPayload.exp && tokenPayload.exp * 1000 < Date.now()) {
                console.log('Token has expired, not connecting WebSocket');
                localStorage.removeItem('access_token');
                navigate('/');
                return;
            }
        } catch (error) {
            console.error('Error parsing token:', error);
            return;
        }

        // Close any previous socket before opening a new one: a leaked
        // handler that keeps reconnecting stacks live connections server-side.
        if (wsRef.current) {
            wsRef.current.onclose = null;
            wsRef.current.close();
        }

        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const ws = new WebSocket(
            `${wsProtocol}//${window.location.host}/api/ws?token=${encodeURIComponent(token)}`
        );
        wsRef.current = ws;

        ws.onopen = () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };

        ws.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        ws.onclose = (event) => {
            // Reconnect only if this socket is still the active one — after
            // unmount or replacement the ref no longer points at it.
            if (wsRef.current !== ws) return;

            // Closed for an authentication reason: back to the sign-in page.
            if (event.code === 1008 || event.code === 4000 || event.code === 4001 ||
                event.reason?.toLowerCase().includes('token') ||
                event.reason?.toLowerCase().includes('auth')) {
                console.log('WebSocket closed due to authentication issue, not reconnecting');
                localStorage.removeItem('access_token');
                navigate('/');
                return;
            }

            reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
        };

        ws.onmessage = (event) => {
            if (event.data === 'ping') {
                ws.send('pong');
            } else {
                // Try to parse as JSON for presence updates
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'ticket_presence_update') {
                        handlePresenceUpdate(data);
                        return;
                    }
                } catch (e) {
                    // Not JSON, treat as regular refresh message
                }

                // Use ref instead of stale closure variable
                if (!isSearchActiveRef.current) {
                    scheduleTicketsRefresh();
                }
            }
        };
    };

    const handlePresenceUpdate = (data) => {
        const { ticket_id, action, viewer } = data;
        const applyPresence = (prevTickets) => prevTickets.map((ticket) => {
            if (ticket._id !== ticket_id) return ticket;
            let updatedViewers = [...(ticket.presence_viewers || [])];
            if (action === 'join') {
                const existingIndex = updatedViewers.findIndex((v) => v.viewer_id === viewer.viewer_id);
                if (existingIndex >= 0) {
                    updatedViewers[existingIndex] = viewer;
                } else {
                    updatedViewers.push(viewer);
                }
            } else if (action === 'leave') {
                updatedViewers = updatedViewers.filter((v) => v.viewer_id !== viewer.viewer_id);
            }
            return { ...ticket, presence_viewers: updatedViewers };
        });
        setTickets(applyPresence);
        setAssignedTickets(applyPresence);
    };

    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            navigate('/');
            return;
        }

        const fetchAllData = async () => {
            try {
                setIsLoading(true);

                const userData = await fetchUserData(token);
                // The person's own defaults (28/10): the view the dashboard
                // opens on and the order of the list, unless the URL already
                // says (a link, the back button). Applied through the URL so
                // the table restores them like a deep link.
                const initialSort = readSortFromUrl();
                const preferences = await fetchPreferences();
                const defaults = {};
                if (!searchParams.get('activeTab') && preferences.default_view_id) {
                    defaults.activeTab = `view:${preferences.default_view_id}`;
                }
                if (!searchParams.get('sortOrder')) {
                    initialSort.sort_by = 'updated_at';
                    const statusParams = searchParams.getAll('statusFilter').filter(Boolean);
                    initialSort.order = orderFor(preferences, statusParams.length === 1 ? statusParams[0] : ORDER_ALL);
                    defaults.sortField = initialSort.sort_by;
                    defaults.sortOrder = initialSort.order;
                }
                if (Object.keys(defaults).length > 0) {
                    const params = new URLSearchParams(searchParams);
                    Object.entries(defaults).forEach(([key, value]) => params.set(key, value));
                    setSearchParams(params, { replace: true });
                }
                // KI-242: render the dashboard shell now — the statistics
                // (DashboardInsights) load in parallel with the ticket list.
                setIsLoading(false);

                const initialFilters = {
                    topics: [],
                    separate_assigned: true,
                    ...initialSort
                };
                setCurrentFilters(initialFilters);

                const promises = [
                    fetchTickets(token, 1, initialFilters),
                    fetchStats(token, { force: true })
                ];

                if (userData?.has_brand) {
                    promises.push(fetchBrands(token));
                }

                await Promise.all(promises);

            } catch (error) {
                console.error('Error loading dashboard data:', error);
            } finally {
                setIsLoading(false);
                setTicketsLoading(false);
            }
        };

        fetchAllData();
        connectWebSocket();

        const pingInterval = setInterval(() => {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send('ping');
            }
        }, 30000);

        return () => {
            clearInterval(pingInterval);
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (pendingRefreshTimeoutRef.current) {
                clearTimeout(pendingRefreshTimeoutRef.current);
            }
            if (wsRef.current) {
                // Null the ref before the async onclose fires so its
                // reconnect guard sees the socket as retired.
                const ws = wsRef.current;
                wsRef.current = null;
                ws.close();
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    useEffect(() => {
        if (location.state?.notification) {
            const { message, success } = location.state.notification;
            setNotificationState({ message, success, visible: true });
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate]);

    // A success toast leaves after 5 s; an error stays until closed.
    useEffect(() => {
        if (notificationState.visible && notificationState.success) {
            const timeoutId = setTimeout(() => {
                setNotificationState((prev) => ({ ...prev, visible: false }));
            }, 5000);

            return () => clearTimeout(timeoutId);
        }
        return undefined;
    }, [notificationState.visible, notificationState.success]);

    const fetchUserData = async (token) => {
        try {
            const response = await axios.get('/api/users/user', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(response.data);
            if (response.data?._id) {
                localStorage.setItem('user_id', response.data._id);
            }
            return response.data;
        } catch (error) {
            console.error('Failed to fetch user data:', error);
            setUser(null);
            return null;
        }
    };

    const fetchTickets = async (token, page = 1, filters = null, assignedPage = 1) => {
        try {
            const ticketsPerPage = 100;
            const activeFilters = filters || currentFilters;

            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: ticketsPerPage.toString(),
            });

            if (activeFilters.separate_assigned) {
                queryParams.append('assigned_page', assignedPage.toString());
                queryParams.append('assigned_limit', ticketsPerPage.toString());
                queryParams.append('separate_assigned', 'true');
            }

            if (activeFilters.topics && activeFilters.topics.length > 0) {
                activeFilters.topics.forEach((topic) => {
                    queryParams.append('topics', topic);
                });
            }

            if (activeFilters.viewId) {
                queryParams.append('view_id', activeFilters.viewId);
            }

            if (activeFilters.is_vip_only) {
                queryParams.append('is_vip_only', 'true');
            }

            // « À traiter » reason filter (DashboardInsights): the snapshot
            // makes the list match the counter that was clicked (KI-242).
            if (activeFilters.attention_reason) {
                queryParams.append('attention_reason', activeFilters.attention_reason);
                if (activeFilters.attention_snapshot) {
                    queryParams.append('attention_snapshot', activeFilters.attention_snapshot);
                }
            }

            appendSortParams(queryParams, activeFilters);

            const response = await axios.get(`/api/tickets/tickets?${queryParams}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data) {
                if (activeFilters.separate_assigned) {
                    setTickets(response.data.unassigned_tickets || []);
                    setAssignedTickets(response.data.assigned_tickets || []);
                    setPaginationInfo(response.data.unassigned_pagination);
                    setAssignedPaginationInfo(response.data.assigned_pagination);
                } else {
                    setTickets(response.data.tickets || []);
                    setAssignedTickets([]);
                    setPaginationInfo(response.data.pagination);
                    setAssignedPaginationInfo(null);
                }

                setCurrentPage(page);
                setAssignedCurrentPage(assignedPage);
            } else {
                setTickets([]);
                setAssignedTickets([]);
                setPaginationInfo(null);
                setAssignedPaginationInfo(null);
            }
        } catch (error) {
            console.error('Failed to fetch tickets:', error);
            setTickets([]);
            setAssignedTickets([]);
            setPaginationInfo(null);
            setAssignedPaginationInfo(null);
        }
    };

    const fetchMyAssignedTickets = async (token, page = 1, filters = null) => {
        try {
            const ticketsPerPage = 100;
            const activeFilters = filters || currentFilters;

            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: ticketsPerPage.toString(),
            });

            if (activeFilters.topics && activeFilters.topics.length > 0) {
                activeFilters.topics.forEach((topic) => {
                    queryParams.append('topics', topic);
                });
            }

            if (activeFilters.viewId) {
                queryParams.append('view_id', activeFilters.viewId);
            }

            if (activeFilters.is_vip_only) {
                queryParams.append('is_vip_only', 'true');
            }

            appendSortParams(queryParams, activeFilters);

            const response = await axios.get(`/api/tickets/tickets/my-assigned?${queryParams}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data) {
                // my-assigned endpoint returns tickets in assignedTickets slot
                setTickets([]);
                setAssignedTickets(response.data.tickets || []);
                setPaginationInfo(null);
                setAssignedPaginationInfo(response.data.pagination);
                setAssignedCurrentPage(page);
            } else {
                setTickets([]);
                setAssignedTickets([]);
                setPaginationInfo(null);
                setAssignedPaginationInfo(null);
            }
        } catch (error) {
            console.error('Failed to fetch my assigned tickets:', error);
            setTickets([]);
            setAssignedTickets([]);
            setPaginationInfo(null);
            setAssignedPaginationInfo(null);
        }
    };


    const fetchBrands = async (token) => {
        try {
            const response = await axios.get('/api/brands/list', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBrands(response.data || []);
            // The first brand is selected by default.
            if (response.data && response.data.length > 0) {
                setSelectedBrand(response.data[0].id);
            }
        } catch (error) {
            console.error('Failed to fetch brands:', error);
            setBrands([]);
        }
    };

    const handleTicketClick = (ticketId) => {
        // Store search params and navigate immediately
        const currentSearch = location.search || '';
        if (currentSearch) {
            sessionStorage.setItem('una_dashboard_search', currentSearch);
        } else {
            sessionStorage.removeItem('una_dashboard_search');
        }
        navigate(`/tickets/${ticketId}/messages`, {
            state: { fromDashboardSearch: location.search }
        });
    };

    // Handlers for the create ticket dialog
    const handleOpenCreateTicketOverlay = () => {
        setIsCreateTicketOverlayOpen(true);
        setSubmitTicketError(null);
        setSubmitTicketSuccess(null);
        setNewTicketData({ tracking: '', message: '', ticket_topic_id: '' });
    };

    const handleCloseCreateTicketOverlay = () => {
        setIsCreateTicketOverlayOpen(false);
    };

    const handleNewTicketInputChange = (e) => {
        const { name, value } = e.target;
        setNewTicketData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmitNewTicket = async (e) => {
        e.preventDefault();
        setIsSubmittingTicket(true);
        setSubmitTicketError(null);
        setSubmitTicketSuccess(null);
        const token = localStorage.getItem('access_token');

        if (!newTicketData.ticket_topic_id) {
            setSubmitTicketError(tl('edn.topicRequired', 'Choisissez un motif.'));
            setIsSubmittingTicket(false);
            return;
        }

        const payload = {
            ...newTicketData,
            ticket_topic_id: parseInt(newTicketData.ticket_topic_id, 10)
        };

        try {
            const response = await axios.post('/api/tickets/tickets/forward_to_envoidunet_create', payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSubmitTicketSuccess(t('ticketCreatedSuccessfully', 'Ticket créé avec succès !') + (response.data?.ticket_id ? ` ID: ${response.data.ticket_id}` : ''));
            setNewTicketData({ tracking: '', message: '', ticket_topic_id: '' });
            setTimeout(() => {
                handleCloseCreateTicketOverlay();
            }, 2500);
        } catch (error) {
            console.error('Failed to create EDN ticket:', error);
            setSubmitTicketError(error.response?.data?.detail || t('failedToCreateEdnTicket', 'Échec de la création du ticket. Veuillez réessayer.'));
        } finally {
            setIsSubmittingTicket(false);
        }
    };

    const handleBrandChange = (brandId) => {
        setSelectedBrand(brandId);

        const newParams = new URLSearchParams(searchParams);
        if (brandId) {
            newParams.set('selectedBrand', brandId.toString());
        } else {
            newParams.delete('selectedBrand');
        }
        setSearchParams(newParams, { replace: true });
    };

    const handleFilterChange = (newFilters) => {
        // Tab/view payloads carry neither the sort nor the attention reason:
        // keep both in effect.
        const updatedFilters = {
            sort_by: currentFilters.sort_by,
            order: currentFilters.order,
            attention_reason: currentFilters.attention_reason || null,
            attention_snapshot: currentFilters.attention_snapshot || null,
            ...newFilters
        };
        setCurrentFilters(updatedFilters);

        if (newFilters.viewMode !== undefined) {
            setCurrentViewMode(newFilters.viewMode);
        }

        const token = localStorage.getItem('access_token');
        if (token) {
            if (newFilters.viewMode === 'my_assigned') {
                fetchMyAssignedTickets(token, 1, updatedFilters);
            } else {
                fetchTickets(token, 1, updatedFilters, 1); // Reset both pages when filtering
            }
        }
    };

    // « À traiter » reason: filter the queue on one attention reason (toggle),
    // as counted by the insights snapshot the reason belongs to.
    const handleAttentionReasonChange = (reason, snapshotId = null) => {
        setAttentionReason(reason);
        const updatedFilters = {
            ...currentFilters,
            attention_reason: reason,
            attention_snapshot: reason ? snapshotId : null
        };
        setCurrentFilters(updatedFilters);
        const token = localStorage.getItem('access_token');
        if (!token) return;
        if (currentViewMode === 'my_assigned') {
            fetchMyAssignedTickets(token, 1, updatedFilters);
        } else {
            fetchTickets(token, 1, updatedFilters, 1);
        }
    };

    // Refetch the current lists (and the counters) after a bulk assign/resolve.
    const refreshTickets = () => {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        if (currentViewMode === 'my_assigned') {
            fetchMyAssignedTickets(token, assignedCurrentPage, currentFilters);
        } else {
            fetchTickets(token, currentPage, currentFilters, assignedCurrentPage);
        }
        fetchStats(token, { force: true });
    };

    const notify = (message, success = true) => {
        setNotificationState({ message, success, visible: true });
    };

    // Column-header sort (KI-111): re-query page 1 with the new order instead
    // of re-sorting the 100 tickets already loaded.
    const handleSortChange = ({ sort_by, order }) => {
        const updatedFilters = { ...currentFilters, sort_by, order };
        setCurrentFilters(updatedFilters);

        const token = localStorage.getItem('access_token');
        if (!token) {
            return;
        }
        if (currentViewMode === 'my_assigned') {
            fetchMyAssignedTickets(token, 1, updatedFilters);
        } else {
            fetchTickets(token, 1, updatedFilters, 1);
        }
    };

    const handlePageChange = (newUnassignedPage, newAssignedPage) => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            return;
        }

        const hasUnassignedChange = newUnassignedPage !== null && newUnassignedPage !== undefined;
        const hasAssignedChange = newAssignedPage !== null && newAssignedPage !== undefined;

        if (!hasUnassignedChange && !hasAssignedChange) {
            return;
        }

        const targetUnassigned = hasUnassignedChange ? newUnassignedPage : currentPage;
        const targetAssigned = hasAssignedChange ? newAssignedPage : assignedCurrentPage;

        if (currentFilters.viewId) {
            fetchTickets(token, targetUnassigned, currentFilters, targetAssigned);
            return;
        }

        if (currentViewMode === 'my_assigned') {
            if (hasAssignedChange && newAssignedPage > 0 && (!assignedPaginationInfo?.total_pages || newAssignedPage <= assignedPaginationInfo.total_pages)) {
                fetchMyAssignedTickets(token, newAssignedPage, currentFilters);
            }
            return;
        }

        if (currentViewMode === 'assigned') {
            if (hasAssignedChange && newAssignedPage > 0 && (!assignedPaginationInfo?.total_pages || newAssignedPage <= assignedPaginationInfo.total_pages)) {
                fetchTickets(token, currentPage, currentFilters, newAssignedPage);
            }
            return;
        }

        if (hasUnassignedChange && newUnassignedPage > 0 && (!paginationInfo?.total_pages || newUnassignedPage <= paginationInfo.total_pages)) {
            fetchTickets(token, newUnassignedPage, currentFilters, assignedCurrentPage);
        }
    };

    const title = t('dashboard', 'Tableau de bord');

    if (isLoading) {
        return (
            <div className="u-page dash-v2" aria-busy="true" aria-label={t('loadingDashboard', 'Chargement du tableau de bord…')}>
                <PageHeader title={title} subtitle={<span className="u-skeleton dash-v2__line-skeleton" />} />
                <div className="u-well">
                    <span className="u-skeleton dash-v2__block-skeleton" />
                    <div className="u-kpi-row">
                        {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton dash-v2__tile-skeleton" />)}
                    </div>
                    <span className="u-skeleton dash-v2__table-skeleton" />
                </div>
            </div>
        );
    }

    const canSeeInsights = canSeeTeamInsights(user);

    // The header line: the open workload, then what waits for the team.
    // One number, one source: the pipeline's total when it is shown (the
    // stage table under the header counts the same tickets), else /stats.
    const pipelineTotal = canSeeInsights ? insightsSummary?.pipeline?.total : undefined;
    const openCount = isCount(pipelineTotal)
        ? pipelineTotal
        : isCount(ticketStats?.remaining_tickets)
            ? ticketStats.remaining_tickets
            : (isCount(insightsSummary?.kpis?.open_backlog?.current) ? insightsSummary.kpis.open_backlog.current : null);
    const attentionCount = canSeeInsights && isCount(insightsSummary?.attention?.total) ? insightsSummary.attention.total : null;
    const headerLoading = ticketStats === undefined || (canSeeInsights && insightsSummary === undefined);
    let workload = null;
    if (openCount === 0) {
        workload = tl('header.noneOpen', 'Aucun ticket ouvert');
    } else if (openCount !== null) {
        const parts = [tl('header.open', '{{value}} tickets ouverts', { count: openCount, value: fmtFr(openCount) })];
        if (attentionCount > 0) {
            parts.push(tl('header.attention', '{{value}} attendent votre équipe', { count: attentionCount, value: fmtFr(attentionCount) }));
        } else if (attentionCount === 0) {
            parts.push(tl('header.attentionNone', 'aucun n’attend votre équipe'));
        }
        workload = parts.join(' · ');
    }
    const subtitle = workload || (headerLoading ? <span className="u-skeleton dash-v2__line-skeleton" /> : null);

    const headerActions = brands.length > 0 ? (
        <label className="dash-v2__brand">
            <Store aria-hidden="true" />
            <span className="dash-v2__brand-label">{t('headerShop', 'Boutique')}</span>
            <select
                className="u-select"
                value={selectedBrand ?? ''}
                onChange={(e) => handleBrandChange(e.target.value || null)}
            >
                {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>{brand.name}</option>
                ))}
            </select>
        </label>
    ) : null;

    return (
        <div className="u-page dash-v2">
            <PageHeader title={title} subtitle={subtitle} actions={headerActions} />

            <div className="u-well">
                {user === null && (
                    <Banner tone="danger">{tl('userError', 'Votre compte n’a pas pu être chargé : rechargez la page.')}</Banner>
                )}
                {canSeeInsights && (
                    <DashboardInsights
                        windowDays={windowDays}
                        onWindowChange={setWindowDays}
                        activeReason={attentionReason}
                        onReasonSelect={handleAttentionReasonChange}
                        onLoaded={setInsightsSummary}
                        showActivity={user?.role !== 'employee'}
                    />
                )}

                <TicketTableDashboard
                    tickets={tickets}
                    assignedTickets={assignedTickets}
                    user={user}
                    onTicketClick={handleTicketClick}
                    hasFaAccount={user?.fa_account}
                    onCreateTicket={handleOpenCreateTicketOverlay}
                    pagination={paginationInfo}
                    assignedPagination={assignedPaginationInfo}
                    onPageChange={handlePageChange}
                    onFilterChange={handleFilterChange}
                    onSortChange={handleSortChange}
                    currentViewMode={currentViewMode}
                    onSearchStateChange={setIsSearchActive}
                    selectedBrand={selectedBrand}
                    ticketStats={ticketStats || null}
                    statsLoading={ticketStats === undefined}
                    pipeline={!canSeeInsights ? null : (insightsSummary === undefined ? undefined : (insightsSummary?.pipeline || null))}
                    attentionReason={attentionReason}
                    onAttentionReasonChange={handleAttentionReasonChange}
                    isLoading={ticketsLoading}
                    onRefresh={refreshTickets}
                    onNotify={notify}
                />
            </div>

            {notificationState.visible && (
                <Toast
                    tone={notificationState.success ? 'success' : 'danger'}
                    onClose={() => setNotificationState((prev) => ({ ...prev, visible: false }))}
                    closeLabel={t('dashboard_dismissNotification', 'Fermer')}
                >
                    {notificationState.message}
                </Toast>
            )}

            {isCreateTicketOverlayOpen && (
                <Modal
                    icon={Plus}
                    title={t('createNewEdnTicketTitle', 'Créer un nouveau ticket EnvoiDuNet')}
                    description={tl('edn.desc', 'Le ticket est créé chez EnvoiDuNet, puis arrive dans la file.')}
                    onClose={handleCloseCreateTicketOverlay}
                    closeLabel={t('common.close', 'Fermer')}
                    footer={(
                        <>
                            <button type="button" onClick={handleCloseCreateTicketOverlay} className="u-btn u-btn--secondary">
                                {t('cancel', 'Annuler')}
                            </button>
                            <button type="submit" form="dash-edn-create" className="u-btn u-btn--primary" disabled={isSubmittingTicket}>
                                {isSubmittingTicket ? t('submitting', 'Envoi en cours...') : t('createTicket', 'Créer le ticket')}
                            </button>
                        </>
                    )}
                >
                    <form id="dash-edn-create" className="u-form" onSubmit={handleSubmitNewTicket}>
                        <div className="u-field">
                            <label className="u-label" htmlFor="tracking">{t('trackingNumber', 'Numéro de suivi')}</label>
                            <input
                                type="text"
                                id="tracking"
                                name="tracking"
                                className="u-input"
                                value={newTicketData.tracking}
                                onChange={handleNewTicketInputChange}
                                required
                            />
                        </div>
                        <div className="u-field">
                            <label className="u-label" htmlFor="ticket_topic_id">{tl('edn.topic', 'Motif')}</label>
                            <select
                                id="ticket_topic_id"
                                name="ticket_topic_id"
                                className="u-select"
                                value={newTicketData.ticket_topic_id}
                                onChange={handleNewTicketInputChange}
                                required
                            >
                                <option value="">{tl('edn.selectTopic', 'Choisir un motif')}</option>
                                {EDN_TICKET_TOPICS.map((topic) => (
                                    <option key={topic.id} value={topic.id}>
                                        {topic.topic_full}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="u-field">
                            <label className="u-label" htmlFor="message">{t('message', 'Message')}</label>
                            <textarea
                                id="message"
                                name="message"
                                className="u-textarea"
                                value={newTicketData.message}
                                onChange={handleNewTicketInputChange}
                                required
                                rows="4"
                            />
                        </div>
                        {submitTicketError && (
                            <div className="u-banner u-banner--danger" role="alert">
                                <CircleAlert strokeWidth={2} aria-hidden="true" />
                                <div className="u-banner__body">{submitTicketError}</div>
                            </div>
                        )}
                        {submitTicketSuccess && <Banner tone="success">{submitTicketSuccess}</Banner>}
                    </form>
                </Modal>
            )}

        </div>
    );
};

export default Dashboard;
